import React, { useState, useEffect, useCallback } from "react";
import axios from "../api/axios";
import {
  FiShoppingBag, FiLoader, FiSearch, FiChevronLeft, FiChevronRight,
  FiCalendar, FiDollarSign, FiPackage
} from "react-icons/fi";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .sp-root {
    font-family: 'Montserrat', sans-serif;
    padding: 24px;
    max-width: 80rem;
    margin: 0 auto;
    min-height: 100vh;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  @media (max-width: 640px) {
    .sp-root {
      padding: 16px;
    }
  }

  /* Header */
  .sp-header {
    margin-bottom: 32px;
  }

  .sp-title {
    font-size: 28px;
    font-weight: 700;
    background: linear-gradient(135deg, #1e293b, #475569);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 8px;
  }

  @media (min-width: 640px) {
    .sp-title {
      font-size: 30px;
    }
  }

  .sp-subtitle {
    color: #6b7280;
  }

  /* Filters */
  .sp-filters {
    margin-bottom: 24px;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .sp-search-wrap {
    position: relative;
    flex: 1;
    min-width: 200px;
  }

  .sp-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
  }

  .sp-search-input {
    width: 100%;
    padding: 12px 16px 12px 40px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Montserrat', sans-serif;
    outline: none;
    transition: all 0.2s;
  }

  .sp-search-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  .sp-filter-select {
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Montserrat', sans-serif;
    outline: none;
    transition: all 0.2s;
    background: #ffffff;
    cursor: pointer;
  }

  .sp-filter-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  /* Purchases Grid */
  .sp-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media (min-width: 768px) {
    .sp-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (min-width: 1024px) {
    .sp-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .sp-empty {
    grid-column: 1 / -1;
    text-align: center;
    padding: 48px 0;
    color: #6b7280;
  }

  .sp-empty-icon {
    font-size: 36px;
    margin: 0 auto 16px;
    color: #d1d5db;
  }

  .sp-empty-text {
    margin: 0;
  }

  /* Purchase Card */
  .sp-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .sp-card-body {
    padding: 24px;
  }

  .sp-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .sp-card-info {
    flex: 1;
  }

  .sp-product-name {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
  }

  .sp-invoice-number {
    font-size: 14px;
    color: #6b7280;
  }

  .sp-status-badge {
    padding: 4px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  .sp-status-completed {
    background: #d1fae5;
    color: #065f46;
  }

  .sp-status-pending {
    background: #fef3c7;
    color: #92400e;
  }

  .sp-status-cancelled {
    background: #fee2e2;
    color: #991b1b;
  }

  .sp-card-details {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .sp-detail-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #6b7280;
  }

  .sp-detail-item svg {
    margin-right: 8px;
    flex-shrink: 0;
  }

  .sp-pv-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
  }

  .sp-pv-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
  }

  /* Pagination */
  .sp-pagination {
    margin-top: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .sp-page-btn {
    padding: 8px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sp-page-btn:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .sp-page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .sp-page-info {
    padding: 8px 16px;
  }

  /* Loading State */
  .sp-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  .sp-loading-content {
    text-align: center;
  }

  .sp-spinner {
    animation: sp-spin 1s linear infinite;
    font-size: 36px;
    color: #3b82f6;
    margin: 0 auto 16px;
    display: block;
  }

  @keyframes sp-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .sp-loading-text {
    color: #6b7280;
  }

  /* Error State */
  .sp-error {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  .sp-error-content {
    text-align: center;
    color: #ef4444;
  }

  .sp-error-text {
    margin-bottom: 16px;
  }

  .sp-retry-btn {
    padding: 8px 16px;
    background: #3b82f6;
    color: #ffffff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    transition: background 0.2s;
  }

  .sp-retry-btn:hover {
    background: #2563eb;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .sp-card-body {
      padding: 16px;
    }

    .sp-card-header {
      flex-direction: column;
      gap: 8px;
    }

    .sp-filters {
      flex-direction: column;
    }

    .sp-search-wrap {
      min-width: unset;
    }

    .sp-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const SelfPurchase = ({ user }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchPurchases();
  }, [currentPage, statusFilter]);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...(statusFilter !== "all" && { status: statusFilter })
      });
      const { data } = await axios.get(`/puap/self-purchases?${params}`);
      setPurchases(data.purchases);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load purchases");
    } finally {
      setLoading(false);
    }
  };

  const filteredPurchases = purchases.filter(p => {
    const q = searchTerm.toLowerCase();
    return (
      (p.productId?.name?.toLowerCase() || "").includes(q) ||
      (p.invoiceNumber?.toLowerCase() || "").includes(q)
    );
  });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (loading) {
    return (
      <div className="sp-loading">
        <style>{styles}</style>
        <div className="sp-loading-content">
          <FiLoader className="sp-spinner" />
          <p className="sp-loading-text">Loading your purchases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sp-error">
        <style>{styles}</style>
        <div className="sp-error-content">
          <p className="sp-error-text">{error}</p>
          <button onClick={fetchPurchases} className="sp-retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getStatusClass = (status) => {
    switch (status) {
      case "completed":
        return "sp-status-completed";
      case "pending":
        return "sp-status-pending";
      case "cancelled":
        return "sp-status-cancelled";
      default:
        return "";
    }
  };

  return (
    <div className="sp-root">
      <style>{styles}</style>

      <div className="sp-header">
        <h1 className="sp-title">Self Purchase</h1>
        <p className="sp-subtitle">View and manage your own product purchases</p>
      </div>

      <div className="sp-filters">
        <div className="sp-search-wrap">
          <FiSearch className="sp-search-icon" />
          <input
            type="text"
            placeholder="Search by product name or invoice..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="sp-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="sp-filter-select"
        >
          <option value="all">All Status</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="sp-grid">
        {filteredPurchases.length === 0 ? (
          <div className="sp-empty">
            <FiShoppingBag className="sp-empty-icon" />
            <p className="sp-empty-text">No purchases found</p>
          </div>
        ) : (
          filteredPurchases.map((purchase) => (
            <div key={purchase._id} className="sp-card">
              <div className="sp-card-body">
                <div className="sp-card-header">
                  <div className="sp-card-info">
                    <h3 className="sp-product-name">
                      {purchase.productId?.name || "Unknown Product"}
                    </h3>
                    <p className="sp-invoice-number">
                      Invoice: {purchase.invoiceNumber || "N/A"}
                    </p>
                  </div>
                  <span className={`sp-status-badge ${getStatusClass(purchase.status)}`}>
                    {purchase.status}
                  </span>
                </div>

                <div className="sp-card-details">
                  <div className="sp-detail-item">
                    <FiPackage />
                    <span>Quantity: {purchase.quantity}</span>
                  </div>
                  <div className="sp-detail-item">
                    <FiDollarSign />
                    <span>Amount: ₹{purchase.totalAmount?.toLocaleString()}</span>
                  </div>
                  <div className="sp-detail-item">
                    <FiCalendar />
                    <span>
                      {purchase.createdAt
                        ? new Date(purchase.createdAt).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                </div>

                {purchase.productId?.pv && (
                  <div className="sp-pv-section">
                    <div className="sp-pv-label">
                      PV: {purchase.productId.pv}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="sp-pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="sp-page-btn"
          >
            <FiChevronLeft />
          </button>
          <span className="sp-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="sp-page-btn"
          >
            <FiChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default SelfPurchase;